import { Eye } from "lucide-react";
import { StatusBadge } from "@/components/fragments/status-badge";
import type { PurchaseOrder } from "@/types/purchaseOrder";

interface MobilePurchaseOrderListProps {
  items: PurchaseOrder[];
  onItemClick: (item: PurchaseOrder) => void;
  onPlaceBid?: (item: PurchaseOrder) => void;
  onViewDetails?: (item: PurchaseOrder) => void;
}

export default function MobilePurchaseOrderList({
  items,
  onItemClick,
  onViewDetails,
}: MobilePurchaseOrderListProps) {
  const handleViewDetails = (item: PurchaseOrder, e: React.MouseEvent) => {
    e.stopPropagation();
    onViewDetails?.(item);
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "accepted":
        return "success";
      case "declined":
        return "danger";
      default:
        return "default";
    }
  };

  const getCustomStatusColors = (status: string) => {
    switch (status) {
      case "pending":
        return {
          background: "#FEF3C7",
          text: "#92400E",
          dot: "#F59E0B",
          border: "#FCD34D",
        };
      case "accepted":
        return {
          background: "#D1FAE5",
          text: "#065F46",
          dot: "#10B981",
          border: "#6EE7B7",
        };
      case "declined":
        return {
          background: "#FEE2E2",
          text: "#991B1B",
          dot: "#EF4444",
          border: "#FCA5A5",
        };
      default:
        return undefined;
    }
  };

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          className="rounded-lg p-4 bg-white border border-gray-200"
        >
          {/* PO Number with Checkbox */}
          <div className="flex items-center gap-2 mb-3">
            <input type="checkbox" className="rounded" />
            <span className="font-medium text-primary text-sm">
              {item.poNumber || "—"}
            </span>
          </div>

          {/* Details Grid */}
          <div className="space-y-2 mb-3">
            {/* Date Received */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">DATE RECEIVED</span>
              <span className="text-sm text-gray-600 font-[300]">
                {item.dateReceived || "—"}
              </span>
            </div>

            {/* Total Amount */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">TOTAL AMOUNT</span>
              <span className="text-sm text-gray-900 font-[400]">
                {item.totalAmount || "—"}
              </span>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">STATUS</span>
              <StatusBadge
                status={item.status}
                variant={getStatusVariant(item.status)}
                customColors={getCustomStatusColors(item.status)}
                showDot={true}
                className="DM-Sans font-[300] border-0"
              />
            </div>
          </div>

          {/* Action Button */}
          <div className="pt-3 border-t border-gray-200">
            <div
              className="flex items-center gap-1 text-gray-500 cursor-pointer hover:text-gray-700"
              onClick={(e) => handleViewDetails(item, e)}
            >
              <Eye className="h-4 w-4" />
              <span className="text-sm">View</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
